import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { addDays } from "@/components/MonthGrid";

const WEEKDAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

export default function RoomBlockDialog({ open, onOpenChange, rooms, defaultRoomId, defaultDate, onSaved }) {
  const [form, setForm] = useState({ room_id: "", start_date: "", end_date: "", reason: "Manutenzione" });
  const [recurring, setRecurring] = useState(false);
  const [weekday, setWeekday] = useState("0");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    const s = defaultDate || "";
    setForm({ room_id: defaultRoomId || "", start_date: s, end_date: s ? addDays(s, 1) : "", reason: "Manutenzione" });
    setRecurring(false);
  }, [open, defaultRoomId, defaultDate]);

  const submit = async () => {
    if (!form.room_id || !form.start_date || !form.end_date) { toast.error("Compila camera e date"); return; }
    if (!recurring && form.end_date <= form.start_date) { toast.error("La data di fine deve essere successiva all'inizio"); return; }
    if (recurring && form.end_date < form.start_date) { toast.error("La fine ricorrenza deve essere successiva all'inizio"); return; }
    setSaving(true);
    try {
      if (recurring) {
        const r = await api.post("/room-blocks/recurring", { ...form, weekday: Number(weekday) });
        const { created, skipped } = r.data;
        toast.success(`Creati ${created.length} blocchi settimanali`, skipped.length ? { description: `${skipped.length} date saltate perché occupate: ${skipped.slice(0, 4).join(", ")}${skipped.length > 4 ? "…" : ""}`, duration: 8000 } : undefined);
      } else {
        await api.post("/room-blocks", form);
        toast.success("Camera bloccata");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="room-block-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">Blocca camera</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Camera</Label>
            <Select value={form.room_id} onValueChange={(v) => set("room_id", v)}>
              <SelectTrigger data-testid="block-room-select" className="mt-1.5"><SelectValue placeholder="Seleziona camera" /></SelectTrigger>
              <SelectContent>{rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox data-testid="block-recurring-toggle" checked={recurring} onCheckedChange={(v) => setRecurring(!!v)} />
            Ripeti ogni settimana (es. pulizia profonda)
          </label>
          {recurring && (
            <div>
              <Label>Giorno della settimana</Label>
              <Select value={weekday} onValueChange={setWeekday}>
                <SelectTrigger data-testid="block-weekday-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{recurring ? "Inizio ricorrenza" : "Dal"}</Label><Input type="date" data-testid="block-start" className="mt-1.5" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
            <div><Label>{recurring ? "Fine ricorrenza (max 1 anno)" : "Al (escluso)"}</Label><Input type="date" data-testid="block-end" className="mt-1.5" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} /></div>
          </div>
          <div><Label>Motivo</Label><Input data-testid="block-reason" className="mt-1.5" value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="Manutenzione" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
          <Button data-testid="block-save-button" onClick={submit} disabled={saving} className="bg-stone-700 hover:bg-stone-800 text-white">{recurring ? "Crea serie" : "Blocca"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
